<script lang="ts" setup>
	type HintList = {
		label: string
		icon: string
		colorClass: string
		bgHex: string
		class?: any
	}

	export type FrameClass = {
		base: string
		overflow: string
		img: Partial<{
			base: string
			width: string
			height: string
		}>
		imgWrapper: Partial<{
			base: string
			height: string
			width: string
			position: string
			rounded: string
		}>
	}

	const configClass: FrameClass = {
		base: '',
		overflow: '',
		img: {
			base: 'object-cover',
			width: '',
			height: 'max-h-[541px]'
		},
		imgWrapper: {
			height: 'min-h-[541px]',
			width: 'w-full sm:min-w-[456px] ',
			position: 'relative',
			rounded: 'rounded '
		}
	}

	const { img, imgWrapper, base, overflow } = configClass
	const { animateStyle = 'style-1', ...props } = defineProps<{
		hintList: HintList[]
		src: string
		alt?: string
		animateStyle?: 'style-1' | 'style-2'
		ui?: Partial<FrameClass>
	}>()
	const activeIndex = ref(0)
	const style2List = ref<(HintList & { active: boolean })[]>([])

	onMounted(() => {
		if (animateStyle === 'style-2') {
			const list = props.hintList.map((item, index) => ({
				...item,
				active: index === 0
			}))
			style2List.value = list
		}
	})
</script>
<template>
	<div :class="[ui?.base ?? base, ui?.overflow ?? overflow, 'relative']">
		<ul
			class="absolute pointer-events-none bottom-20 lg:-left-28 left-0 flexed flex-col z-10"
			:class="{ 'rounded-[6px]': animateStyle == 'style-2' }"
		>
			<template v-if="animateStyle === 'style-1'">
				<li
					v-for="(item, index) in hintList"
					:key="'hints-' + index"
					class="py-5 px-3 mb-2 justify-between w-full gap-x-5 anime flex items-center h-[40px] bg-white"
					:style="{
						transform: `translateX(${20 * index}px)`,
						'--animation-delay': `${(index + 1) * 2}s`,
						'--animation-color': item.bgHex
					}"
				>
					<div class="flexed relative z-10 gap-x-2 font-medium text-xs" :class="item.class">
						<span class="flex-none h-[4px] w-[4px] rounded-full block" :class="item.colorClass" />
						<span>{{ item.label }}</span>
					</div>
					<span
						class="relative z-10 animate-icon"
						:style="{
							'--shake-icon-animation-delay': `${(index + 1) * 3}s`
						}"
					>
						<tools-icon dir="random" :icon="item.icon" />
					</span>
				</li>
			</template>

			<li
				v-else
				v-for="(item, index) in style2List"
				:key="'effect-' + index"
				class="flex list-effect items-center justify-between gap-x-5 w-full custom-transition-2 bg-white px-2 overflow-hidden"
				:class="{ active: item.active, 'h-[0px]': activeIndex !== index }"
			>
				<div class="flex items-center gap-x-2">
					<div
						class="xs:h-[28px] w-[20px] h-[20px] xs:w-[28px] flex justify-center items-center rounded-full"
						:class="item.colorClass"
					>
						<tools-icon
							:icon="item.icon"
							dir="random"
							:height="18"
							:width="18"
							svg-class="sm:w-[18px] sm:h-[18px] w-[16px] h-[16px]"
						/>
					</div>
					<span class="xs:text-xs text-[10px] font-medium text-health-900" :class="item.class">
						{{ item.label }}
					</span>
				</div>

				<div class="flex items-center gap-x-3 flex-none">
					<div
						class="min-w-[36px] bg-health-100 rounded-[20px] relative h-1 overflow-hidden progress-wrapper"
						:class="item.class"
					>
						<div
							class="progress absolute top-0 bottom-0 my-auto h-full w-[20px] rounded-[inherit] custom-transition-2"
							:class="item.colorClass ?? 'bg-health-500'"
						/>
					</div>

					<div class="flex-none">
						<slot name="right-icon">
							<tools-icon dir="random" icon="star-rainbow" />
						</slot>
					</div>
				</div>
			</li>
		</ul>

		<div
			:class="[
				ui?.imgWrapper?.base ?? imgWrapper.base,
				ui?.imgWrapper?.height ?? imgWrapper.height,
				ui?.imgWrapper?.position ?? imgWrapper.position,
				ui?.imgWrapper?.rounded ?? imgWrapper.rounded,
				ui?.imgWrapper?.width ?? imgWrapper.width
			]"
		>
			<nuxt-img
				provider="cloudinary"
				loading="lazy"
				:src="src"
				:alt="alt || ''"
				:class="[ui?.img?.base ?? img.base, ui?.img?.height ?? img.height, ui?.img?.width ?? img.width]"
				preload
			/>
		</div>
	</div>
</template>

<style scoped>
	:root {
		--animation-delay: 0;
		--shake-icon-animation-delay: 0;
		--animation-color: #d3ebf8;
	}
	.list-effect.active {
		@apply sm:h-[40px] sm:py-7 py-2 rounded-[3.5px];
	}
	.anime {
		position: relative;
		@apply before:absolute before:w-0 before:bg-[var(--animation-color)] before:h-full before:left-0 before:opacity-90 before:transition-all;
	}
	.anime::before {
		animation: fillWidth 5s linear infinite alternate;
		animation-delay: var(--animation-delay);
	}

	.animate-icon {
		position: relative;
		transform-origin: center;
		transform-style: preserve-3d;
		transition: 2s linear;
		animation: shakeIcon 2s ease-in-out infinite alternate;
		animation-delay: var(--shake-icon-animation-delay);
		transform: scale(1) rotate(0);
	}

	@keyframes fillWidth {
		0% {
			width: 0;
		}
		100% {
			width: 100%;
		}
	}
	@keyframes shakeIcon {
		0% {
			transform: scale(1) rotate(0);
		}
		100% {
			transform: scale(0.78) rotate(20deg);
		}
	}
</style>
